import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Skeleton } from '@/components/ui/skeleton';
import { PlanCard } from '../components/PlanCard';
import { Plan } from '@/types';

const transformPlans = (plansData: any[]): Plan[] => {
  return plansData.map(plan => ({
    ...plan,
    features: Array.isArray(plan.features) ? plan.features.map(f => String(f)) : []
  }));
};

const SeoPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [randomPlans, setRandomPlans] = useState<Plan[]>([]);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      setIsLoading(true);
      
      // Fetch the SEO page
      const { data, error } = await supabase
        .from('seo_pages')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .single();

      if (error || !data) {
        throw new Error('Page not found');
      }

      setPage(data);
      
      // Fetch random featured plans
      const { data: plansData, error: plansError } = await supabase
        .from('plans')
        .select('*')
        .order('popularity', { ascending: false })
        .limit(10);
        
      if (plansError) throw plansError;
      
      // Transform plans to match Plan type and randomize
      if (plansData && plansData.length > 0) {
        const transformedPlans = transformPlans(plansData);
        
        // Randomly select 3 plans
        const shuffled = [...transformedPlans].sort(() => 0.5 - Math.random());
        setRandomPlans(shuffled.slice(0, 3));
      }
      
    } catch (error) {
      console.error('Error fetching page:', error);
      navigate('/404');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-3xl mx-auto">
            <Skeleton className="h-12 w-3/4 mb-8" />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-[400px]" />
              ))}
            </div>
            
            <Skeleton className="h-72 w-full mb-8" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/6" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!page) return null;

  const canonicalUrl = `https://abonemt.dk/${page.slug}`;
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{page.meta_title}</title>
        <meta name="description" content={page.meta_description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={page.meta_title} />
        <meta property="og:description" content={page.meta_description} />
        {page.image_url && <meta property="og:image" content={page.image_url} />}
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={canonicalUrl} />
        <meta property="twitter:title" content={page.meta_title} />
        <meta property="twitter:description" content={page.meta_description} />
        {page.image_url && <meta property="twitter:image" content={page.image_url} />}
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <Navbar />

      <main className="flex-1 container mx-auto px-4 md:px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
            {page.title}
          </h1>
          
          {/* Random offers */}
          {randomPlans.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">Populære mobilabonnementer</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {randomPlans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} />
                ))}
              </div>
            </div>
          )}
          
          {/* Featured image */}
          {page.image_url && (
            <img
              src={page.image_url}
              alt={page.title}
              className="w-full h-auto rounded-lg mb-8 object-cover"
            />
          )}
          
          {/* Content */}
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SeoPage;
